import { beginAction, errorReason } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';

import { API_URL } from './consts';

/**
 * Runtime OAuth provider identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/oauth.rs.
 */
export const OAUTH_PROVIDER = {
  Google: 'google',
  Meta: 'meta',
} as const;

export type OAuthProvider = (typeof OAUTH_PROVIDER)[keyof typeof OAUTH_PROVIDER];

/** Narrows unknown values so callers can safely branch without casting. */
function isOAuthProvider(value: unknown): value is OAuthProvider {
  return (
    typeof value === 'string' && Object.values(OAUTH_PROVIDER).includes(value as OAuthProvider)
  );
}

/** Coerces URL query params into a typed provider; returns null for unsupported values. */
export function stringToProvider(providerStr?: string | null): OAuthProvider | null {
  return isOAuthProvider(providerStr) ? providerStr : null;
}

/** OAuth flow intents that must stay in sync with the backend enum. */
export enum OAuthIntent {
  Login = 'login',
  Register = 'register',
  LinkAccount = 'link_account',
}

/** Narrows unknown values to the OAuth intents accepted by the backend. */
function isOAuthIntent(value: unknown): value is OAuthIntent {
  return typeof value === 'string' && Object.values(OAuthIntent).includes(value as OAuthIntent);
}

/** Defaults to Login for missing/unrecognized intents so the OAuth flow never stalls. */
export function stringToIntent(intentStr?: string): OAuthIntent {
  return isOAuthIntent(intentStr) ? intentStr : OAuthIntent.Login;
}

/**
 * Runtime OAuth callback status identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/oauth.rs.
 */
export const OAuthResponseStatus = {
  // Success cases
  LoginSuccess: 'login_success',
  AccountLinked: 'account_linked',

  // Expected "error" cases that need special handling
  OAuthIdentityConflict: 'oauth_identity_conflict',
  NoExistingAccount: 'no_existing_account',
  SessionExpired: 'session_expired',
  NotLoggedIn: 'not_logged_in',
  ProviderMismatch: 'provider_mismatch',
  InvalidState: 'invalid_state',
  RegistrationRequired: 'registration_required',
} as const;

export type OAuthResponseStatus = (typeof OAuthResponseStatus)[keyof typeof OAuthResponseStatus];

/** Narrows unknown values to callback statuses emitted by the backend. */
function isOAuthResponseStatus(value: unknown): value is OAuthResponseStatus {
  return (
    typeof value === 'string' &&
    Object.values(OAuthResponseStatus).includes(value as OAuthResponseStatus)
  );
}

interface OAuthUrlResponse {
  authorization_url: string;
}

interface CallbackRequest {
  provider: OAuthProvider;
  oauth_code: string;
  state: string;
}

/**
 * OAuth identity information returned by callbacks and sent during registration.
 * Must match OAuthIdentityInfo in rust/server/src/api/structs.rs.
 */
export interface OAuthIdentityInfo {
  provider: OAuthProvider;
  provider_user_id: string;
  email?: string;
  name?: string;
  picture?: string;
}

interface UserInfo {
  username: string;
  is_new_user: boolean;
  email?: string;
  name?: string;
}

/**
 * Response from the callback endpoint.
 * Must be kept in sync with the corresponding struct in rust/server/src/oauth.rs.
 */
interface CallbackResponse {
  status: OAuthResponseStatus;
  intent: OAuthIntent;
  provider: OAuthProvider;

  user_info?: UserInfo;
  oauth_identity?: OAuthIdentityInfo;
  invite_code?: string;
}

/** Callback result augmented with transport and contract-validation state. */
type OAuthCallbackResult = CallbackResponse & { rpcError: boolean };

/** Untyped callback payload shape prior to runtime validation. */
interface RawCallbackResponse {
  status?: unknown;
  intent?: unknown;
  provider?: unknown;
  user_info?: unknown;
  oauth_identity?: unknown;
  invite_code?: unknown;
  rpcError?: unknown;
}

/** Defensive narrowing — callback payloads can have arbitrary types per-field. */
function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** Validates raw identity fields before trusting them as typed OAuthIdentityInfo. */
function normalizeOAuthIdentityInfo(rawIdentity: unknown): OAuthIdentityInfo | undefined {
  if (typeof rawIdentity !== 'object' || rawIdentity === null) {
    return undefined;
  }

  const candidate = rawIdentity as Record<string, unknown>;
  const provider = stringToProvider(optionalString(candidate.provider));
  const provider_user_id = optionalString(candidate.provider_user_id);

  if (!provider || !provider_user_id) {
    return undefined;
  }

  const email = optionalString(candidate.email);
  const name = optionalString(candidate.name);
  const picture = optionalString(candidate.picture);
  if (
    (candidate.email !== undefined && email === undefined) ||
    (candidate.name !== undefined && name === undefined) ||
    (candidate.picture !== undefined && picture === undefined)
  ) {
    return undefined;
  }

  return {
    provider,
    provider_user_id,
    ...(email === undefined ? {} : { email }),
    ...(name === undefined ? {} : { name }),
    ...(picture === undefined ? {} : { picture }),
  };
}

/** Validates user information nested in a callback response. */
function normalizeUserInfo(rawUserInfo: unknown): UserInfo | undefined {
  if (typeof rawUserInfo !== 'object' || rawUserInfo === null) return undefined;

  const candidate = rawUserInfo as Record<string, unknown>;
  if (typeof candidate.username !== 'string' || typeof candidate.is_new_user !== 'boolean') {
    return undefined;
  }

  const email = optionalString(candidate.email);
  const name = optionalString(candidate.name);
  if (
    (candidate.email !== undefined && email === undefined) ||
    (candidate.name !== undefined && name === undefined)
  ) {
    return undefined;
  }

  return {
    username: candidate.username,
    is_new_user: candidate.is_new_user,
    ...(email === undefined ? {} : { email }),
    ...(name === undefined ? {} : { name }),
  };
}

/** Validates and normalizes the exact callback response emitted by Rust. */
function normalizeCallbackResponse(value: unknown): CallbackResponse | null {
  if (typeof value !== 'object' || value === null) return null;

  const raw = value as RawCallbackResponse;
  const provider = stringToProvider(optionalString(raw.provider));
  if (!isOAuthResponseStatus(raw.status) || !isOAuthIntent(raw.intent) || !provider) return null;

  const userInfo = raw.user_info === undefined ? undefined : normalizeUserInfo(raw.user_info);
  const oauthIdentity =
    raw.oauth_identity === undefined ? undefined : normalizeOAuthIdentityInfo(raw.oauth_identity);
  const inviteCode = optionalString(raw.invite_code);
  if (
    (raw.user_info !== undefined && userInfo === undefined) ||
    (raw.oauth_identity !== undefined && oauthIdentity === undefined) ||
    (raw.invite_code !== undefined && inviteCode === undefined) ||
    raw.rpcError !== undefined
  ) {
    return null;
  }

  return {
    status: raw.status,
    intent: raw.intent,
    provider,
    ...(userInfo === undefined ? {} : { user_info: userInfo }),
    ...(oauthIdentity === undefined ? {} : { oauth_identity: oauthIdentity }),
    ...(inviteCode === undefined ? {} : { invite_code: inviteCode }),
  };
}

/** Kicks off the OAuth redirect — returns the provider's authorization URL or an rpcError flag. */
export async function initiateOAuthLogin(
  provider: OAuthProvider,
  intent: OAuthIntent,
  inviteCode?: string
): Promise<{ url: string; rpcError?: boolean }> {
  // Feedback breadcrumb: record the OAuth sign-in start attempt now; outcome is marked success/failure below.
  const action = beginAction(FEEDBACK_ACTION.START_OAUTH_SIGN_IN, { provider });

  // Construct URL with query parameters
  let url = `${API_URL}/oauth/prepare?provider=${provider}&intent=${intent}`;

  // Add optional parameters if provided
  if (inviteCode) {
    url += `&invite_code=${encodeURIComponent(inviteCode)}`;
  }

  // Make the API request with all parameters
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    let responseText = '';
    try {
      responseText = await response.clone().text();
    } catch (e) {
      // Ignore
    }

    const rpcError = !response.ok;

    if (rpcError) {
      action.failed({ error: `Server returned HTTP ${response.status}` });
      console.error('OAuth prepare error:', responseText);
      return {
        url: '',
        rpcError: true,
      };
    }

    const data: OAuthUrlResponse = await response.json();

    action.succeeded();

    // The server provides a complete authorization URL which already includes
    // the state parameter for session tracking through the OAuth flow
    return {
      url: data.authorization_url,
      rpcError: false,
    };
  } catch (error) {
    action.failed({ error: errorReason(error) });
    throw error;
  }
}

/**
 * Completes the OAuth exchange. Normalizes the raw backend response into a
 * typed CallbackResponse, handling edge cases like duplicate code redemption
 * and malformed identity payloads without throwing.
 */
export async function handleOAuthCallback(
  provider: OAuthProvider,
  oauth_code: string,
  state: string
): Promise<OAuthCallbackResult> {
  // Feedback breadcrumb: record the OAuth callback attempt now; outcome is marked success/failure below.
  const action = beginAction(FEEDBACK_ACTION.COMPLETE_OAUTH_SIGN_IN, { provider });

  // The state parameter from the provider is our session state key

  const requestBody: CallbackRequest = {
    provider,
    oauth_code,
    state, // The state parameter is our session key
  };

  let response: Response;
  try {
    response = await fetch(`${API_URL}/oauth/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });
  } catch (error) {
    action.failed({ error: errorReason(error) });
    console.error('OAuth callback request failed:', error);

    // Instead of throwing, return a structured error response
    return {
      status: OAuthResponseStatus.InvalidState, // Use as generic error status
      intent: OAuthIntent.Login,
      provider,
      rpcError: true,
    };
  }

  // Try to parse the response as JSON, but handle non-JSON responses
  let responseData: CallbackResponse;
  let hasInvalidCallbackContract = false;
  let responseText = '';
  try {
    // First try to get the text to help with debugging
    responseText = await response.clone().text();

    // Then try to parse as JSON
    const rawResponseData: unknown = await response.json();
    const normalizedResponse = normalizeCallbackResponse(rawResponseData);
    if (normalizedResponse === null) {
      hasInvalidCallbackContract = true;
      responseData = {
        status: OAuthResponseStatus.InvalidState,
        intent: OAuthIntent.Login,
        provider,
      };
    } else {
      responseData = normalizedResponse;
    }
  } catch (error) {
    action.failed({ error: 'Could not read the sign-in response' });
    console.error('Failed to parse OAuth callback response as JSON:', error);
    console.error('Response text was:', responseText);

    // Instead of throwing, return a structured error response
    return {
      status: OAuthResponseStatus.InvalidState, // Use as generic error status
      intent: OAuthIntent.Login,
      provider,
      rpcError: true,
    };
  }

  // Check for transport error or specific OAuth token exchange error
  const hasTransportError = !response.ok;
  const hasTokenExchangeError = responseText.includes('OAuth token exchange error');
  const isCodeAlreadyUsed =
    response.status === 409 ||
    responseText.includes('OAuth code already used') ||
    responseText.includes('code has been used') ||
    responseText.includes('invalid_grant');

  // Special case: if it's just a "code already used" error and we got a successful response,
  // don't treat it as an RPC error - this is a normal condition from duplicate exchanges
  if (isCodeAlreadyUsed && !hasInvalidCallbackContract) {
    console.warn('OAuth code already used, but we have valid response data. Proceeding normally.');
  }

  const result: OAuthCallbackResult = {
    ...responseData,
    rpcError:
      !isCodeAlreadyUsed || hasInvalidCallbackContract
        ? hasInvalidCallbackContract || hasTransportError || hasTokenExchangeError
        : false,
  };

  // Log errors for debugging
  if (result.rpcError) {
    action.failed({
      error: hasInvalidCallbackContract
        ? 'Sign-in response was malformed'
        : hasTokenExchangeError
          ? 'OAuth token exchange failed'
          : hasTransportError
            ? `Server returned HTTP ${response.status}`
            : // TODO(1): These fallback arms are currently unreachable because every
              // rpcError path is classified above by malformed-contract, token-exchange,
              // or transport checks, and the duplicate-code branch clears rpcError.
              isCodeAlreadyUsed
              ? 'OAuth code was already used'
              : 'Sign-in failed',
    });
    console.error('OAuth callback error detected:');
    if (hasTransportError) {
      console.error(`- HTTP status: ${response.status}`);
    }
    if (hasTokenExchangeError) {
      console.error('- Token exchange error detected in response');
    }
    if (isCodeAlreadyUsed) {
      console.warn('- OAuth code already used (this may be due to duplicate requests)');
    }
    console.error('- Response text:', responseText);
  } else {
    action.succeeded();
  }

  return result;
}

// All state is managed server-side.  `state` is an id passed through
// the OAuth provider's state parameter and returned back to us in the callback.
